import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { FilterPanel } from '@gsa/afp-component-library';
import VendorSearchTypeahead from './filter-items/vendorNameTypeahead';
import FederalStandardCodeFilterItem from './filter-items/fed-standard-code-filter';
import VehicleGroupFilterItem from './filter-items/vehicle-group-filter';
import VehicleTypeFilterItem from './filter-items/vehicle-type-filter';
import { useFederalStandards } from '../fvs-provider';
import useFilterPanelReset from '../../../hooks/use-filter-panel-reset';
import { useFvsPreBidSelectionsReport } from './pre-bid-report-provider';
import { STANDARD_ITEM_TYPEAHEAD_OPTIONS_SEARCH } from '../../../services/data-store.gql';
import {
  FED_STANDARD_CODE_FILTER,
  VEHICLE_GROUP_FILTER,
  VEHICLE_TYPE_FILTER,
  FEDERAL_STANDARDS_HIERARCHY,
} from './filter-items/filter-item-constants';

const PreBidReportFilters = ({ setFilters }) => {
  const {
    selectedOptions,
    selectedYear,
    vehicleClassificationOptions,
    applicableFvsYears,
    setFvsContextData,
  } = useFederalStandards();

  const { setFVSPreBidReportContextData } = useFvsPreBidSelectionsReport();

  const { FilterPanel: PreBidReportFilterPanel } = FilterPanel;
  const params = useParams();
  const { year } = params;

  const [typeaheadData, setTypeaheadData] = React.useState([]);
  const [sinWithIds, setSinWithIds] = React.useState([]);
  const [, setError] = React.useState(null);
  const appliedFiltersRef = React.useRef(null);

  const defaultYear = applicableFvsYears?.find(
    (y) => y.isDefault === true,
  ) ?? { value: new Date().getFullYear() };

  const filterStructure = useMemo(() => [
    {
      key: 'year',
      title: 'Contract year',
      type: 'select',
      value: selectedYear?.toString() || defaultYear?.value?.toString(),
      permanent: false,
      operator: '$or',
      options: applicableFvsYears?.map((y) => ({
        label: y.label,
        value: y.value,
      })),
      expanded: true,
      hideClear: true,
    },
    {
      key: 'vendor',
      title: 'Vendor name',
      id: 'vendorName',
      ariaLabel: 'Search by Vendor name',
      value: '',
      operator: 'EQ',
      component: VendorSearchTypeahead,
      isSingleValueFilter: true,
      permanent: false,
      expanded: true,
      hideClear: true,
      customFieldProps: {
        inputCharNum: 2,
        help: 'Search by Vendor name or Unique Entity Identifier',
      },
    },
    {
      key: 'intentionToBid',
      title: 'Intention to bid',
      type: 'multiselect',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
      value: [],
      operator: '$or',
      expanded: true,
      hideClear: true,
    },
    {
      key: 'standardItemNumber',
      title: 'Standard Item',
      type: 'typeahead',
      value: '',
      operator: 'EQ',
      expanded: true,
      hideClear: true,
      customFieldProps: { inputCharNum: 1 },
    },
    {
      title: 'Federal standards',
      key: FEDERAL_STANDARDS_HIERARCHY,
      expanded: true,
      filters: [
        {
          key: FED_STANDARD_CODE_FILTER,
          title: 'Federal Standard Code',
          component: FederalStandardCodeFilterItem,
          permanent: false,
          operator: '$exact',
          value: selectedOptions?.fedStandardCode || '',
        },
        {
          key: VEHICLE_GROUP_FILTER,
          title: 'Vehicle group',
          component: VehicleGroupFilterItem,
          permanent: false,
          operator: '$or',
          value: selectedOptions?.vehicleGroup || '',
        },
        {
          key: VEHICLE_TYPE_FILTER,
          title: 'Vehicle type',
          component: VehicleTypeFilterItem,
          permanent: false,
          operator: '$or',
          value: selectedOptions?.vehicleType || '',
        },
      ],
    },
  ], [vehicleClassificationOptions, selectedYear, applicableFvsYears, selectedOptions]);

  const [getFilterTypeAheadOptions] = useLazyQuery(
    STANDARD_ITEM_TYPEAHEAD_OPTIONS_SEARCH,
    {
      fetchPolicy: 'network-only',
      onError: () => {
        setError('Error fetching Standard Item type-ahead options');
        setTypeaheadData([]);
      },
      onCompleted: ({ getStandardItemTypeAheadOptions }) => {
        if (getStandardItemTypeAheadOptions) {
          const { options, key, optionsWithIds } =
            getStandardItemTypeAheadOptions;
          setSinWithIds(optionsWithIds);
          setTypeaheadData({ field: key, values: options });
        }
      },
    },
  );

  const handleFiltersChange = (updatedFilters) => {
    // Flatten filters, including nested ones from hierarchy
    const filterByKeys = updatedFilters?.reduce((filterObj, currentFilterObj) => {
      const result = { ...filterObj, [currentFilterObj?.key]: currentFilterObj };
      // If this filter has nested filters (hierarchy), flatten them
      if (currentFilterObj?.filters && Array.isArray(currentFilterObj.filters)) {
        currentFilterObj.filters.forEach((nestedFilter) => {
          result[nestedFilter?.key] = { ...nestedFilter };
        });
      }
      return result;
    }, {});

    const appliedFilters = {
      operator: 'AND',
      value: [],
    };

    let optionsChanged = false;
    const newOptions = { ...selectedOptions };

    // Handle year change
    if (filterByKeys?.year?.value) {
      appliedFilters.value.push({
        key: '$preBidSelection.contract_year$',
        operator: 'EQ',
        value: Number.parseInt(filterByKeys?.year?.value, 10),
      });
      setFvsContextData('SET_SELECTED_YEAR', Number.parseInt(filterByKeys?.year?.value, 10));
      setFVSPreBidReportContextData(
        'SET_PBR_SELECTED_YEAR',
        Number.parseInt(filterByKeys?.year?.value, 10),
      );
    }

    // Handle vendor
    if (filterByKeys?.vendor?.value) {
      appliedFilters.value.push({
        key: '$preBidSelection.vendor_id$',
        operator: 'EQ',
        value: filterByKeys?.vendor?.value,
      });
    }

    // Handle federal standard code
    const fedStandardCodeChanged =
      filterByKeys?.[FED_STANDARD_CODE_FILTER]?.value !== selectedOptions?.fedStandardCode;

    if (filterByKeys?.[FED_STANDARD_CODE_FILTER]?.value) {
      const value = [filterByKeys?.[FED_STANDARD_CODE_FILTER]?.value];
      appliedFilters.value.push({
        key: '$preBidSelection.standardItem.fed_standard_code$',
        operator: 'IN',
        value,
      });
      newOptions.fedStandardCode = filterByKeys?.[FED_STANDARD_CODE_FILTER]?.value;
      setFVSPreBidReportContextData('SET_SELECTED_CODES', value);
      setFVSPreBidReportContextData('SET_VEHICLE_GROUP_REPORTING', '');

      if (fedStandardCodeChanged) {
        // When fedStandardCode changes, clear vehicleGroup and vehicleType
        newOptions.vehicleGroup = '';
        newOptions.vehicleType = '';
        optionsChanged = true;
      }
    }

    // Handle vehicle group
    const vehicleGroupChanged =
      filterByKeys?.[VEHICLE_GROUP_FILTER]?.value !== selectedOptions?.vehicleGroup;

    if (filterByKeys?.[VEHICLE_GROUP_FILTER]?.value) {
      appliedFilters.value.push({
        key: '$preBidSelection.vehicle_group_code$',
        operator: 'EQ',
        value: filterByKeys?.[VEHICLE_GROUP_FILTER]?.value,
      });
      newOptions.vehicleGroup = filterByKeys?.[VEHICLE_GROUP_FILTER]?.value;
      setFVSPreBidReportContextData(
        'SET_VEHICLE_GROUP_REPORTING',
        filterByKeys?.[VEHICLE_GROUP_FILTER]?.value,
      );
      setFVSPreBidReportContextData('SET_SELECTED_CODES', []);

      if (vehicleGroupChanged) {
        // When vehicleGroup changes, clear vehicleType
        newOptions.vehicleType = '';
        optionsChanged = true;
      }
    }

    // Handle vehicle type
    if (filterByKeys?.[VEHICLE_TYPE_FILTER]?.value) {
      appliedFilters.value.push({
        key: '$preBidSelection.standardItem.vehicle_type$',
        operator: 'EQ',
        value: filterByKeys?.[VEHICLE_TYPE_FILTER]?.value,
      });
      newOptions.vehicleType = filterByKeys?.[VEHICLE_TYPE_FILTER]?.value;
      optionsChanged = true;
    }

    // Handle intention to bid
    if (filterByKeys?.intentionToBid?.value) {
      const newValues = filterByKeys?.intentionToBid?.value?.map((v) =>
        v === 'Yes' ? 1 : 0,
      );
      appliedFilters.value.push({
        key: '$preBidSelection.intent_to_bid$',
        operator: 'IN',
        value: newValues,
      });
    }

    // Handle standard item
    if (filterByKeys?.standardItemNumber?.value) {
      const sinId = sinWithIds?.find(
        (sin) =>
          sin.standardItemNumber === filterByKeys?.standardItemNumber?.value,
      )?.id;
      appliedFilters.value.push({
        key: '$preBidSelection.standard_item_id$',
        operator: 'IN',
        value: [sinId],
      });
    }

    // Update context if options changed
    if (optionsChanged) {
      setFvsContextData('SET_SELECTED_OPTIONS', newOptions);
    }

    // the contract year is the default filter and always stays applied, even
    // when the panel reports every filter as cleared
    if (!filterByKeys?.year?.value && selectedYear) {
      appliedFilters.value.push({
        key: '$preBidSelection.contract_year$',
        operator: 'EQ',
        value: Number.parseInt(selectedYear, 10),
      });
    }

    // skip redundant refetches when the applied filters did not actually change
    const appliedFiltersHash = JSON.stringify(appliedFilters);
    if (appliedFiltersRef.current === appliedFiltersHash) {
      return;
    }
    appliedFiltersRef.current = appliedFiltersHash;

    setFilters({ ...appliedFilters });
  };

  // 'Reset All' keeps the contract year and clears every other filter, the
  // selections carried over from the Federal Vehicle Standards page included.
  const { filterPanelKey, handleClearAll } = useFilterPanelReset(() => {
    setFvsContextData('SET_SELECTED_OPTIONS', {
      ...selectedOptions,
      fedStandardCode: '',
      vehicleGroup: '',
      vehicleType: '',
    });
    setFVSPreBidReportContextData('SET_SELECTED_CODES', []);
    setFVSPreBidReportContextData('SET_VEHICLE_GROUP_REPORTING', '');
  });

  const handleFetchTypeaheads = ({ variables: queryVariables }) => {
    const selectedKey = queryVariables?.field;
    const filterObj = queryVariables?.filters[0]?.conditions?.find(
      (filter) => filter.key === selectedKey,
    );

    if (filterObj?.key === 'standardItemNumber') {
      const { value } = filterObj;
      const variables = {
        key: selectedKey,
        search: value,
        filters: [
          {
            operator: '$and',
            conditions: [
              { operator: '$exact', key: 'year', value: `${selectedYear}` },
            ],
          },
        ],
      };
      getFilterTypeAheadOptions({ variables });
    }
  };

  return (
    <PreBidReportFilterPanel
      key={`pre-bid-report-filters-${filterPanelKey}`}
      clearButtonLabel="Reset All"
      filterStructure={filterStructure}
      model="PreBidSelection"
      setQueryFiltersState={handleFiltersChange}
      fetchTypeaheads={handleFetchTypeaheads}
      typeaheadData={typeaheadData}
      handleClearAll={handleClearAll}
      showClearIcon
      retainFilters
    />
  );
};

PreBidReportFilters.propTypes = {
  setFilters: PropTypes.func.isRequired,
};

export default PreBidReportFilters;
